import {
	keys,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

type StorageEntrySize = {
	keyFamily: string;
	keyChars: number;
	valueChars: number;
	entryChars: number;
};

export type LocalStorageQuotaSnapshot = {
	capturedAt: number;
	sizeUnit: 'utf16_code_units';
	snapshotStatus: 'complete' | 'partial' | 'unavailable';
	keyCount: number;
	scannedKeyCount: number;
	totalEntryChars: number;
	largestKeys: StorageEntrySize[];
	attemptedKeyFamily?: string;
	attemptedValueChars?: number;
};

const knownKeys = Object.entries({ ...keys, ...PERSISTED_KEYS_ON_SERVER });
const SCAN_BUDGET_MS = 4;
const statsigFamilies = [
	'statsig.failed_logs',
	'statsig.pending_events',
	'statsig.failed_shutdown_events',
	'statsig.cached.evaluations',
	'statsig.stable_id',
];

function getKeyFamily(key: string): string {
	const statsigFamily = statsigFamilies.find(
		(family) => key === family || key.startsWith(`${family}.`),
	);
	if (statsigFamily) return statsigFamily;
	// Only emit known constants, never a user ID, SDK hash, or arbitrary key name.
	return (
		knownKeys.find(([, knownKey]) => key === knownKey || key.endsWith(`.${knownKey}`))?.[0] ??
		'unknown'
	);
}

/**
 * Scan once at the first failure, before Refresh can clear storage. Stop between reads after 4ms;
 * native synchronous storage calls cannot be interrupted. Partial totals cover scanned entries only.
 * Best-effort size estimates, not the browser's quota accounting. Never retain storage values.
 */
export function captureLocalStorageQuotaSnapshot(
	attemptedKey?: string,
	attemptedValueChars?: number,
): LocalStorageQuotaSnapshot {
	const deadline = performance.now() + SCAN_BUDGET_MS;
	const largestEntries: (Omit<StorageEntrySize, 'keyFamily'> & { key: string })[] = [];
	const snapshot: LocalStorageQuotaSnapshot = {
		capturedAt: Date.now(),
		sizeUnit: 'utf16_code_units',
		snapshotStatus: 'complete',
		keyCount: 0,
		scannedKeyCount: 0,
		totalEntryChars: 0,
		largestKeys: [],
		attemptedKeyFamily: attemptedKey === undefined ? undefined : getKeyFamily(attemptedKey),
		attemptedValueChars,
	};
	try {
		const storage = window.localStorage;
		snapshot.keyCount = storage.length;
		for (let index = 0; index < snapshot.keyCount; index++) {
			if (performance.now() >= deadline) {
				snapshot.snapshotStatus = 'partial';
				break;
			}
			try {
				const key = storage.key(index);
				const value = key === null ? null : storage.getItem(key);
				if (key === null || value === null) {
					snapshot.snapshotStatus = 'partial';
					continue;
				}
				const entryChars = key.length + value.length;
				snapshot.scannedKeyCount++;
				snapshot.totalEntryChars += entryChars;
				if (largestEntries.length < 3 || entryChars > largestEntries[2].entryChars) {
					largestEntries.push({ key, keyChars: key.length, valueChars: value.length, entryChars });
					largestEntries.sort((left, right) => right.entryChars - left.entryChars);
					largestEntries.length = Math.min(largestEntries.length, 3);
				}
			} catch {
				snapshot.snapshotStatus = 'partial';
			}
		}
	} catch {
		snapshot.snapshotStatus = 'unavailable';
	}
	// Classify only the retained three entries. Raw names exist only during this scan.
	snapshot.largestKeys = largestEntries.map(({ key, ...size }) => ({
		keyFamily: getKeyFamily(key),
		...size,
	}));
	return snapshot;
}
